import { useState } from 'react';
import { useProductPageContext } from '../../../hooks/useProductPageContext';
import { ReviewT } from '@/settings/types';
import { getProductReviews } from '@/lib/use-product-reviews';
import { logger } from '@/lib/logger';
import ReviewCard from './ReviewCard';
import Loading from '@/components/commons/Loading';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

const ProductReviews = () => {
  const {
    additionalInfo,
    product: { psku, name: pageProductName },
  } = useProductPageContext();

  const [reviews, setReviews] = useState<ReviewT[]>(
    additionalInfo?.reviews?.reviews ?? [],
  );
  const [loading, setLoading] = useState(false);

  const countAbove3andAHalf =
    additionalInfo?.reviews?.summary?.countAbove3andAHalf ?? 0;

  const handleLoadMore = async () => {
    if (!reviews?.length || !countAbove3andAHalf) {
      return;
    }
    setLoading(true);

    try {
      const paginatedReviews = await getProductReviews({
        psku,
        lastIndex: Math.min(reviews.length - 1, countAbove3andAHalf - 1),
        batchSize: 4,
      });

      if (paginatedReviews) {
        setReviews([...reviews, ...paginatedReviews.reviews]);
      }
    } catch (error) {
      logger.error({ error }, 'error paginating product reviews');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {reviews.map((review) => {
        const {
          title,
          stars,
          text,
          createdAt,
          customerName,
          purchaseDate,
          color,
          storage,
          condition,
        } = review;

        return (
          <ReviewCard
            key={review.id}
            title={title}
            stars={stars}
            text={text}
            createdAt={createdAt}
            customerName={customerName}
            purchaseDate={purchaseDate}
            offer={{
              title: pageProductName ?? '',
              color: color ?? '',
              storage: storage ?? '',
              condition: condition ?? '',
            }}
          />
        );
      })}
      {reviews.length < countAbove3andAHalf && (
        <button
          className="flex h-10 items-center justify-center rounded border border-gray-700 text-xs font-medium transition duration-150 ease-in-out hover:bg-gray-700 hover:text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => handleLoadMore()}
          disabled={loading}
        >
          {loading ? <Loading size="xs" /> : t`Load More Reviews`}
        </button>
      )}
    </div>
  );
};

export default ProductReviews;
